import React, { useMemo } from 'react';
import { Button, Checkbox, Input } from '@billda/design-system';
import { SearchIcon, ClearIcon } from '@billda/design-system';

interface TransferListItem {
  id: string;
  name: string;
}

interface TransferListProps {
  availableItems: TransferListItem[];
  assignedItems: TransferListItem[];
  searchText: string;
  onSearchChange: (text: string) => void;
  onMoveToAssigned: (itemId: string) => void;
  onMoveToAvailable: (itemId: string) => void;
  loading?: boolean;
}

const TransferList: React.FC<TransferListProps> = ({
  availableItems,
  assignedItems,
  searchText,
  onSearchChange,
  onMoveToAssigned,
  onMoveToAvailable,
  loading = false,
}) => {
  const assignedIds = useMemo(
    () => new Set(assignedItems.map(item => item.id)),
    [assignedItems]
  );
  const allItems = useMemo(
    () =>
      [...assignedItems, ...availableItems].sort((left, right) => {
        const assignmentOrder =
          Number(assignedIds.has(right.id)) - Number(assignedIds.has(left.id));
        return assignmentOrder || left.name.localeCompare(right.name);
      }),
    [assignedItems, availableItems, assignedIds]
  );
  const filteredItems = allItems.filter(item =>
    item.name.toLowerCase().includes(searchText.trim().toLowerCase())
  );

  const clearSearch = () => {
    onSearchChange('');
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h4 className="type-body-lg font-medium">Labour items</h4>
        <span className="type-caption text-content-secondary">
          {assignedItems.length} selected
        </span>
      </div>

      <Input
        inputSize="sm"
        placeholder="Search labour items…"
        value={searchText}
        onChange={event => onSearchChange(event.target.value)}
        disabled={loading}
        data-testid="transfer-list-search"
        aria-label="Search labour items"
        startAdornment={
          <SearchIcon
            aria-hidden="true"
            className="size-4 text-content-secondary"
          />
        }
        endAdornment={
          searchText ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={clearSearch}
              disabled={loading}
              aria-label="Clear search"
              className="size-6 p-0.5"
            >
              <ClearIcon aria-hidden="true" className="size-3.5" />
            </Button>
          ) : null
        }
      />

      <ul
        className="m-0 max-h-80 list-none divide-y divide-border-default overflow-y-auto rounded-lg border border-border-default p-0"
        data-testid="labour-items-checklist"
      >
        {filteredItems.length === 0 ? (
          <li className="type-body-md px-3 py-6 text-center text-content-secondary">
            {searchText
              ? 'No labour items match your search'
              : 'No labour items available'}
          </li>
        ) : (
          filteredItems.map(item => {
            const assigned = assignedIds.has(item.id);
            const controlId = `group-labour-item-${item.id}`;
            return (
              <li key={item.id} data-testid="labour-item-option">
                <label
                  htmlFor={controlId}
                  className="flex cursor-pointer items-center gap-3 px-3 py-2.5 hover:bg-surface-raised"
                >
                  <Checkbox
                    id={controlId}
                    checked={assigned}
                    onCheckedChange={() => {
                      if (assigned) {
                        onMoveToAvailable(item.id);
                      } else {
                        onMoveToAssigned(item.id);
                      }
                    }}
                    disabled={loading}
                    aria-label={`${assigned ? 'Remove' : 'Assign'} ${item.name}`}
                  />
                  <span className="type-body-md min-w-0 flex-1 truncate">
                    {item.name}
                  </span>
                  <span className="type-caption text-content-secondary">
                    {assigned ? 'Assigned' : 'Available'}
                  </span>
                </label>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
};

export default TransferList;
